import { Icon } from '@iconify/react'
import { observe } from '@legendapp/state'
import { Computed, useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { ChevronDown } from 'lucide-react'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { deleteRole, fetchRolesList, updateRoleStatus } from '~features/roles/api'
import { Roles } from '~features/roles/types'
import {
  Button,
  Card,
  CardContent,
  DialogComponent,
  DropdownMenu,
  DropdownMenuComponent,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~in-app/components'
import {
  FilterDropdownProps,
  TableContextProvider,
  TableState$,
} from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'

export const Route = createFileRoute('/console/roles/list')({
  component: RolesPage,
  staticData: {
    privileges: ['0.3.5.0'],
  },
})
const columnHelper = createColumnHelper<Roles>()

function RolesPage() {
  const navigate = Route.useNavigate()
  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    const filteredQueries = new URLSearchParams(window.location.search).get('queries') || ''
    state$.queries.set(filteredQueries)

    const disposer = observe(state$, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])
  const queryClient = useQueryClient()
  const updateRoleStatusFunc = () => {
    const mutation = useMutation({
      mutationFn: ({ id, status }: { id: string, status: boolean }) => updateRoleStatus(id, status),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['roles-list'] })
        toast.success('Roles status updated successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
        toast.error(`Error updating Roles status: ${error.message}`, { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
    })

    return mutation
  }
  const updateMutation = updateRoleStatusFunc()

  const handleUpdateStatus = (id: string, status: boolean) => {
    updateMutation.mutate({ id, status })
  }

  const useDeleteRoles = () => {
    const mutation = useMutation({
      mutationFn: deleteRole,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['roles-list'] })
        toast.success('Role Deleted Successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const deleteMutation = useDeleteRoles()
  const handleDeleteRoles = (values: Roles) => {
    state$.deleteRoleId.set(values.id)
    state$.deleteRoleName.set(values.name)
    state$.isOpenDeleteRole.set(true)
  }
  const { refetch } = useQuery({
    queryKey: ['roles-list'],
    queryFn: async ({ signal }) => {
      const params = {
        page: state$.pagination.get() || 0,
        size: state$.pageSize.get() || 20,
        queries: state$.queries.get() || undefined,
        sort: state$.sortList.get()
          .map((sortByVal) => {
            return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
          })
          .join(','),
      }
      const response = await fetchRolesList({ query: params })
      const newTableList = (response?.list ?? []).map((item: Roles) => ({
        ...item,
        action: '',
      })) as Roles[]
      tableState$.data.set(newTableList)
      tableState$.totalItem.set(response?.totalItem || 0)
      tableState$.totalPage.set(response?.totalPage || 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return newTableList
    },
  })

  const dropdownItems = (value: Roles) => {
    return [
      {
        label: 'Edit',
        onClick: () =>
          navigate({ to: `/console/roles/edit/${value.id}` }),
        disabled: !auth$.privileges.get()?.has('0.3.5.2'),
        icon: (
          <Icon
            height="16"
            icon="lucide:edit-2"
            width="16"
          />
        ),
      },
      {
        label: value.active ? 'Deactivate' : 'Activate',
        onClick: () => handleUpdateStatus(value.id, !value.active),
        disabled: auth$.privileges.get()?.has('0.3.5.3') ? value.staticFlag : true,
        icon: value.active
          ? (
              <Icon
                height="16"
                icon="lucide:circle-x"
                width="16"
              />
            )
          : (
              <Icon
                height="16"
                icon="lucide:circle-check"
                width="16"
              />
            ),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteRoles(value),
        disabled: auth$.privileges.get()?.has('0.3.5.3') ? value.staticFlag : true,
        icon: (
          <Icon
            height="16"
            icon="lucide:trash-2"
            width="16"
          />
        ),
      },
    ]
  }

  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('name', {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('active', {
      cell: info => (info.getValue() ? 'Active' : 'Inactive'),
      header: () => <span>Status</span>,
      enableSorting: true,
      size: 110,
    }),
    columnHelper.accessor('desp', {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('remarks', {
      cell: info => info.getValue(),
      header: () => <span>Remarks</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('multiTenantFlag', {
      cell: info => (info.getValue() ? 'Yes' : 'No'),
      header: () => <span>Multi Tenant</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('staticFlag', {
      cell: info => (info.getValue() ? 'Yes' : 'No'),
      header: () => <span>Static</span>,
      enableSorting: true,
      size: 120,
    }),
    columnHelper.accessor('createdOn', {
      cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'),
      header: () => <span>Created On</span>,
      enableSorting: true,
      size: 140,
    }),
    columnHelper.accessor('updatedOn', {
      cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'),
      header: () => <span>Updated On</span>,
      enableSorting: true,
      size: 140,

    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            buttonIcon={<Icon icon="lucide:ellipsis-vertical" />}
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
          />
        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]
  const state$ = useObservable({
    sortList: [
      { id: 'name', desc: true },
      { id: 'status', desc: true },
    ],
    pagination: 0,
    pageSize: 20,
    queries: '',
    isChange: false,
    isOpenDeleteRole: false as boolean,
    deleteRoleId: undefined as string | undefined,
    deleteRoleName: undefined as string | undefined,
  })

  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      value: new URLSearchParams(window.location.search).get('queries') || '',
      result: new URLSearchParams(window.location.search).get('queries') || '',
    },
  ]
  const tableState$ = useObservable<TableState$<Roles>>(() => ({
    id: 'roles-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    totalPage: 0,
    pageSize: state$.pageSize.get(),
    pagination: state$.pagination.get(),
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, any> = {}

      const textFilter = value.find((item: any) => item.label === 'Search')
      const textValue = textFilter?.result || ''
      state$.queries.set(textValue)
      state$.isChange.set(!state$.isChange.get())
      searchParams.queries = textValue
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textFilter?.result) delete updatedSearch.queries
          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> Roles Page</span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeleteRole.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete this '${state$.deleteRoleName.get()}' role?`}
                onClose={() => { state$.isOpenDeleteRole.set(false), state$.deleteRoleId.set(undefined), state$.deleteRoleName.set('') }}
                onSubmit={
                  () => {
                    const deleteRoleId = state$.deleteRoleId.get()
                    if (deleteRoleId) {
                      deleteMutation.mutate(deleteRoleId as string)
                      state$.deleteRoleId.set(undefined)
                      state$.deleteRoleName.set(undefined)
                      state$.isOpenDeleteRole.set(false)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <div className="flex items-center">
          <Button
            className="rounded-r-none border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
            color="muted"
            disabled={!auth$.privileges.get()?.has('0.3.5.1')}
            onClick={() => navigate({ to: '/console/roles/add' })}
          >
            Add
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="rounded-l-none border border-l-0 border-slate-400 bg-transparent px-1 font-medium text-muted-foreground hover:opacity-100"
                color="muted"
              >
                <ChevronDown size={15} />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
              <DropdownMenuItem
                disabled={!auth$.privileges.get()?.has('0.3.5.5')}
                onClick={() => navigate({ to: '/console/roles/assign' })}
              >
                Assign Priviledges
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
